import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, fmtDateTime, go, KIND_LABEL, TYPE_LABEL, useServerEvents } from '../lib/util';

const KINDS = ['fact', 'statement', 'rumor', 'theory', 'belief'];

export function EntityDetail({ campaignId, entityId }: { campaignId: string; entityId: string }) {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [edit, setEdit] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);
  const load = async () => {
    try {
      setD(await api(`/api/campaigns/${campaignId}/entities/${entityId}`));
      setErr('');
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    api(`/api/campaigns/${campaignId}/settings`)
      .then((s: any) => setPlayers(s.players))
      .catch(() => undefined);
  }, [entityId]);
  useServerEvents(campaignId, (w) => w === 'records' && void load());
  if (!d) return <div className="page">{err ? <p className="error">{err}</p> : <p className="muted">Loading…</p>}</div>;
  const e = d.entity;
  const act = async (fn: () => Promise<unknown>) => {
    setErr('');
    try {
      await fn();
      await load();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const byKind = KINDS.map((k) => [k, d.claims.filter((c: any) => c.kind === k && c.role === 'subject')] as [string, any[]]).filter(([, l]) => l.length);
  const knows = d.claims.filter((c: any) => c.role === 'knower');
  return (
    <div className="page narrow">
      <p>
        <a href={`#/c/${campaignId}/records/${e.type}`}>← {TYPE_LABEL[e.type]} records</a>
      </p>
      {edit ? (
        <EditEntity e={e} players={players} onCancel={() => setEdit(false)} onSave={(patch) => act(async () => { await api(`/api/campaigns/${campaignId}/entities/${e.id}`, { method: 'PATCH', body: patch }); setEdit(false); })} />
      ) : (
        <div className="page-head">
          <div>
            <h1>{e.name}</h1>
            <p className="muted">
              {TYPE_LABEL[e.type]}
              {e.status && e.status !== 'active' ? ` · ${e.status}` : ''} · {e.visibility === 'gm_only' ? 'GM only' : 'Public'} · {e.origin === 'ai' ? 'created by AI from notes' : 'created by you'}
              {d.player ? ` · played by ${d.player.name}` : ''}
            </p>
          </div>
          <div className="row">
            <button className="btn" onClick={() => setEdit(true)}>
              Edit
            </button>
            <button
              className="btn danger"
              onClick={() => {
                if (confirm(`Delete ${e.name}? This can be undone from History.`)) void act(async () => { await api(`/api/campaigns/${campaignId}/entities/${e.id}`, { method: 'DELETE' }); go(`/c/${campaignId}/records`); });
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
      {err && <p className="error">{err}</p>}
      {e.description && !edit && <p className="description">{e.description}</p>}

      <section>
        <h2>Aliases</h2>
        <ul className="row wrap plain">
          {d.aliases.map((a: any) => (
            <li key={a.id} className="chip">
              {a.alias}
              {a.alias.toLowerCase() !== e.name.toLowerCase() && (
                <button className="link small" aria-label={`Remove alias ${a.alias}`} onClick={() => void act(() => api(`/api/campaigns/${campaignId}/entities/${e.id}/aliases/${a.id}`, { method: 'DELETE' }))}>
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
        <AddAlias onAdd={(alias) => act(() => api(`/api/campaigns/${campaignId}/entities/${e.id}`, { method: 'PATCH', body: { addAliases: [alias] } }))} />
      </section>

      {e.type === 'quest' && (
        <section>
          <h2>Objectives</h2>
          <Objectives objectives={e.data.objectives ?? []} onSave={(objectives) => act(() => api(`/api/campaigns/${campaignId}/entities/${e.id}`, { method: 'PATCH', body: { objectives } }))} />
        </section>
      )}

      <section>
        <h2>What is recorded</h2>
        {byKind.length === 0 && <p className="muted">No claims yet.</p>}
        {byKind.map(([kind, list]) => (
          <div key={kind} className="claim-group">
            <h3>{KIND_LABEL[kind]}</h3>
            {kind !== 'fact' && <p className="muted small">{kind === 'statement' ? 'Confirms only that it was said, not that it is true.' : 'Not a confirmed fact.'}</p>}
            <ul>
              {list.map((c: any) => (
                <Claim key={c.id} c={c} campaignId={campaignId} onChanged={load} />
              ))}
            </ul>
          </div>
        ))}
        <AddClaim campaignId={campaignId} entityId={e.id} onAdded={load} />
      </section>

      {knows.length > 0 && (
        <section>
          <h2>Knows about</h2>
          <ul>
            {knows.map((c: any) => (
              <li key={c.id}>
                <span className="tag subtle">{KIND_LABEL[c.kind]}</span> {c.statement}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2>Mentioned in</h2>
        {d.mentions.length === 0 && <p className="muted">No notes mention this record yet.</p>}
        <ul className="mentions">
          {d.mentions.map((m: any) => (
            <li key={m.id}>
              <a href={`#/c/${campaignId}/s/${m.session_id}`} className="muted small">
                Session {m.session_number} · {fmtDateTime(m.captured_at)}
              </a>
              <div>{m.text}</div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2>Change history</h2>
        <ul className="history-mini">
          {d.history.map((h: any) => (
            <li key={h.seq} className={classNames(h.undone_by_id && 'undone')}>
              <span className="muted small">{fmtDateTime(h.created_at)}</span> {h.summary} <span className="tag subtle">{h.origin}</span>
              {h.note && <span className="muted small"> — {h.note}</span>}
            </li>
          ))}
        </ul>
        <a href={`#/c/${campaignId}/history`}>Full history and undo →</a>
      </section>
    </div>
  );
}

function Claim({ c, campaignId, onChanged }: { c: any; campaignId: string; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const patch = async (body: any) => {
    try {
      await api(`/api/campaigns/${campaignId}/claims/${c.id}`, { method: 'PATCH', body });
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  return (
    <li className={classNames('claim', `st-${c.status}`)}>
      <div>
        {c.holder_text && <span className="muted">{c.holder_text}: </span>}
        {c.statement}
        {c.status !== 'active' && <span className="tag warn-tag">{c.status}</span>}
        {c.certainty !== 'confirmed' && <span className="tag subtle">{c.certainty}</span>}
        {c.visibility === 'gm_only' && <span className="tag secret">GM only</span>}
        {c.origin === 'user' || c.user_edited_at ? <span className="tag subtle">by you</span> : null}
        <button className="link small" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? 'less' : `sources (${c.sources.length})`}
        </button>
      </div>
      {open && (
        <div className="claim-more">
          {c.sources.map((s: any, i: number) => (
            <p key={i} className="small source-line">
              Session {s.sessionNumber ?? '?'} · {s.capturedAt ? fmtDateTime(s.capturedAt) : ''}: “{s.text}”{s.quote ? ` (quote: “${s.quote}”)` : ''}
              {s.outdated && <span className="tag subtle">note edited since</span>}
            </p>
          ))}
          {c.knowers.length > 0 && <p className="small">Known by: {c.knowers.map((k: any) => k.name).join(', ')}</p>}
          <div className="row wrap">
            <label className="small">
              Kind{' '}
              <select value={c.kind} onChange={(e) => void patch({ kind: e.target.value })}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="small">
              Certainty{' '}
              <select value={c.certainty} onChange={(e) => void patch({ certainty: e.target.value })}>
                <option value="confirmed">confirmed</option>
                <option value="likely">likely</option>
                <option value="uncertain">uncertain</option>
              </select>
            </label>
            {c.status !== 'retracted' && (
              <button className="btn small" onClick={() => void patch({ status: 'retracted' })}>
                Retract
              </button>
            )}
          </div>
          {err && <p className="error small">{err}</p>}
        </div>
      )}
    </li>
  );
}

function AddClaim({ campaignId, entityId, onAdded }: { campaignId: string; entityId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ statement: '', kind: 'fact' });
  const [err, setErr] = useState('');
  if (!open)
    return (
      <button className="link" onClick={() => setOpen(true)}>
        + Add a fact, rumor or theory
      </button>
    );
  return (
    <form
      className="row wrap"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await api(`/api/campaigns/${campaignId}/claims`, { body: { ...f, entityIds: [entityId] } });
          setF({ statement: '', kind: f.kind });
          setOpen(false);
          onAdded();
        } catch (x) {
          setErr(errorText(x));
        }
      }}
    >
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} aria-label="Kind">
        {KINDS.map((k) => (
          <option key={k} value={k}>
            {KIND_LABEL[k]}
          </option>
        ))}
      </select>
      <input value={f.statement} onChange={(e) => setF({ ...f, statement: e.target.value })} placeholder="Statement" required aria-label="Statement" className="grow" />
      <button className="btn primary small">Add</button>
      {err && <p className="error small">{err}</p>}
    </form>
  );
}

function AddAlias({ onAdd }: { onAdd: (a: string) => Promise<void> }) {
  const [v, setV] = useState('');
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) void onAdd(v.trim()).then(() => setV(''));
      }}
    >
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="Add alias or shorthand" aria-label="New alias" />
      <button className="btn small">Add</button>
    </form>
  );
}

function Objectives({ objectives, onSave }: { objectives: any[]; onSave: (o: any[]) => Promise<void> }) {
  const [v, setV] = useState('');
  return (
    <div>
      <ul className="objectives">
        {objectives.map((o, i) => (
          <li key={o.id ?? i}>
            <label>
              <input type="checkbox" checked={!!o.done} onChange={() => void onSave(objectives.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))} /> {o.text}
            </label>
          </li>
        ))}
      </ul>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (v.trim()) void onSave([...objectives, { text: v.trim(), done: false }]).then(() => setV(''));
        }}
      >
        <input value={v} onChange={(e) => setV(e.target.value)} placeholder="New objective" aria-label="New objective" />
        <button className="btn small">Add</button>
      </form>
    </div>
  );
}

function EditEntity({ e, players, onSave, onCancel }: { e: any; players: any[]; onSave: (p: any) => void; onCancel: () => void }) {
  const [f, setF] = useState({ name: e.name, type: e.type, status: e.status, description: e.description, visibility: e.visibility, playerId: e.player_id ?? '' });
  return (
    <form
      className="card form-grid"
      onSubmit={(ev) => {
        ev.preventDefault();
        onSave({ ...f, playerId: f.playerId || null });
      }}
    >
      <label>
        Name
        <input value={f.name} onChange={(x) => setF({ ...f, name: x.target.value })} required />
      </label>
      <label>
        Type
        <select value={f.type} onChange={(x) => setF({ ...f, type: x.target.value })}>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label>
        Status
        <input value={f.status} onChange={(x) => setF({ ...f, status: x.target.value })} list="status-options" />
        <datalist id="status-options">
          {['active', 'open', 'completed', 'failed', 'answered', 'dead', 'missing'].map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </label>
      <label>
        Visibility
        <select value={f.visibility} onChange={(x) => setF({ ...f, visibility: x.target.value })}>
          <option value="public">Public</option>
          <option value="gm_only">GM only</option>
        </select>
      </label>
      {f.type === 'pc' && (
        <label>
          Played by
          <select value={f.playerId} onChange={(x) => setF({ ...f, playerId: x.target.value })}>
            <option value="">—</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="span2">
        Description (your own words)
        <textarea value={f.description} onChange={(x) => setF({ ...f, description: x.target.value })} rows={4} />
      </label>
      <div className="span2 row">
        <button className="btn primary">Save</button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
